import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router";
import FormLineDiagram from "./FormLineDiagram";
import type {Field, Section} from "./FormRenderer";
import {ResizeGripper, useResizablePanel} from "../Common/ResizablePanel";
import {useReportUnsavedChanges} from "../../util/UnsavedChanges";
import {Typography} from "@mui/material";
import Axios from "../../util/Axios";
import FormRenderer from "./FormRenderer";
import {Problem} from "../../util/formProblems";
import {REVISION_ACTIONS} from "../Common/LifecycleActions";
import CCButton from "../Common/CCButton";
import css_buttons from "../Common/css/CCButton.module.scss";
import {Grey6, Grey7, Grey17} from "../../theme/styles";
import {askAssistant, openGuide} from "../../data/assistant";

/**
 * The answers a record holds, drawn from the FORM it was seeded from.
 *
 * A 422 IS AN ANSWER, NOT AN ERROR. The server refuses the whole write and returns every problem at
 * once with a path on each, so a person fixing a record sees the full list rather than discovering
 * it one round trip at a time. The problems land on the fields they name.
 *
 * READ-ONLY OUTSIDE DRAFT, which mirrors the backend rather than enforcing it: editing a revision's
 * own fields is a DRAFT-only operation and the server rejects the write regardless of what this
 * shows. Hiding the controls only avoids offering a change that will be refused.
 */

type Props = {
    objectRevisionUSID: string;
    /** The record's lifecycle status. Anything but DRAFT draws read-only. */
    status?: string;
};

export default function ObjectFormTab({objectRevisionUSID, status}: Props) {
    const navigate = useNavigate();
    const [values, setValues] = useState<Record<string, any> | null>(null);
    const [recordName, setRecordName] = useState<string>("");
    // WHETHER THIS CALLER MAY DESIGN THIS RECORD'S FORM -- an owner, on a draft, editable for the
    // persona they act as. The server decides (Get Record Form Design); the tab only asks, so the
    // button is never offered to somebody the save would refuse.
    const [design, setDesign] = useState<{editable: boolean; reason?: string | null} | null>(null);
    // How this record's questions differ from the shape it was seeded from. See DivergencePanel.
    const [divergence, setDivergence] = useState<any>(null);
    const [promoting, setPromoting] = useState<string | null>(null);
    // What promotion actually did, per code. Needed because a successful promotion often leaves the
    // panel looking identical -- see below.
    const [promotedNote, setPromotedNote] = useState<Record<string, string>>({});
    const [edited, setEdited] = useState<Record<string, any>>({});
    const [problems, setProblems] = useState<Problem[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    // The definition the renderer drew, so the diagram beside it describes THAT form. See onLoaded.
    const [drawn, setDrawn] = useState<{fields: Field[]; sections: Section[]}>(
        {fields: [], sections: []});
    // WHETHER THE RENDERER HAS REPORTED YET, which is not the same as `drawn.fields.length === 0`.
    // `drawn` starts empty, so an empty-state keyed on the count alone would flash "no fields" on
    // every form for as long as the first load takes.
    const [reported, setReported] = useState<boolean>(false);
    // WHICH LEVEL IS LIT, AND IN WHAT COLOUR. The colour belongs to the box that was clicked, so
    // it travels with the choice rather than being re-derived here from whether the usid happens
    // to be the last one -- which is the same fact worked out twice.
    const [lit, setLit] = useState<{usid: string | null; colour: string}>(
        {usid: null, colour: "#1997D6"});

    // Same panel behaviour as Filter By, on the other side -- see useResizablePanel's `side`.
    const line = useResizablePanel({storageKey: "formLine", defaultWidth: 236, minWidth: 180,
                                    maxWidth: 420, collapseAt: 150, side: "right"});

    const readOnly = String(status ?? "").toUpperCase() !== "DRAFT";

    const load = useCallback(() => {
        setLoading(true);
        Axios({method: "GET", url: "t1/v1/object_form_input",
               params: {ObjectRevisionUSID: objectRevisionUSID}})
            .then((response: any) => {
                const held = response?.data?.Values ?? {};
                setValues(held);
                setRecordName(String(response?.data?.Name ?? ""));
                Axios({method: "GET", url: "t1/v1/object_form_design", params: {ObjectRevisionUSID: objectRevisionUSID}})
                    .then((r: any) => setDesign({editable: !!r?.data?.Editable, reason: r?.data?.Reason ?? null}))
                    .catch(() => setDesign(null));
                setEdited(held);
                setDivergence(response?.data?.Divergence ?? null);
                setError(null);
                setLoading(false);
            })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? "This record's answers could not be loaded.");
                setLoading(false);
            });
    }, [objectRevisionUSID]);

    useEffect(load, [load]);

    // DERIVED, NOT TRACKED. `values` is what the server last gave us and `edited` is what is on
    // screen, so the difference between them IS the answer -- no third flag to keep in step, and
    // typing a character and deleting it correctly reads as unchanged, which a touched flag gets
    // wrong. JSON.stringify is sound here because both sides come from the same JSON round trip,
    // so key order matches; it is not a general deep-equal.
    const touched = values !== null && JSON.stringify(edited) !== JSON.stringify(values);

    // Reported up so the lifecycle buttons at the top of the record can ask before Release, Revise
    // or Submit throws this away. See util/UnsavedChanges.
    useReportUnsavedChanges("object-form", "Form", !readOnly && touched);

    const save = () => {
        setSaving(true);
        setSaved(false);
        Axios({method: "PUT", url: "t1/v1/object_form_input",
               data: {ObjectRevisionUSID: objectRevisionUSID, Values: edited}})
            .then(() => {
                setProblems([]);
                setError(null);
                setSaved(true);
                setSaving(false);
                setValues(edited);
            })
            .catch((e: any) => {
                const body = e?.response?.data ?? {};
                // The refusal as the server phrased it. Problems draw on their own fields; a message
                // is only shown when there are none to draw, so the page never says the same thing
                // twice.
                setProblems(body.Problems ?? []);
                setError(body.Problems ? null
                                       : (body.Cause ?? "This record could not be saved."));
                setSaving(false);
            });
    };

    // TAKE UP what the form or shape now asks. The server appends the questions to this record's
    // own schema and answers with the divergence recomputed; the form is remounted so it draws
    // the new questions (its definition is re-read on a value change, and no value changed).
    const [adopting, setAdopting] = useState(false);
    const [adoptTick, setAdoptTick] = useState(0);
    const adopt = (codes: string[] | null) => {
        setAdopting(true);
        Axios({method: "POST", url: "t1/v1/object_form_promotion",
               data: {ObjectRevisionUSID: objectRevisionUSID, Action: "Adopt",
                      Codes: codes ?? undefined}})
            .then((response: any) => {
                setDivergence(response?.data?.Divergence ?? divergence);
                setAdoptTick(tick => tick + 1);
                setError(null);
            })
            .catch((e: any) => setError(e?.response?.data?.Cause
                                        ?? "The questions could not be taken up."))
            .finally(() => setAdopting(false));
    };

    const promote = (code: string) => {
        setPromoting(code);
        Axios({method: "POST", url: "t1/v1/object_form_promotion",
               data: {ObjectRevisionUSID: objectRevisionUSID, Code: code}})
            .then((response: any) => {
                // The server recomputes it; taking its answer back beats guessing what changed.
                setDivergence(response?.data?.Divergence ?? divergence);
                // SAY WHAT HAPPENED, because the panel usually looks the same afterwards. Promotion
                // lands on a DRAFT of the shape, and the record is measured against the RELEASED
                // one -- so the field goes on being listed until somebody releases that draft. With
                // no note, a promotion that worked is indistinguishable from a button that did
                // nothing, and the obvious response is to press it again.
                setPromotedNote(previous => ({
                    ...previous,
                    [code]: response?.data?.Created === false
                        ? "Already on a draft of the shape, waiting to be released."
                        : "Added to a draft of the shape. Release that draft to finish.",
                }));
                setPromoting(null);
            })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? "That field could not be promoted.");
                setPromoting(null);
            });
    };

    if (loading) {
        return <Typography style={{fontSize: 14, color: Grey7, marginTop: 24}}>Loading…</Typography>;
    }

    if (values === null) {
        return <Typography style={{fontSize: 14, color: "#B3261E", marginTop: 24}}>{error}</Typography>;
    }

    return (
        <div style={{marginTop: 24, marginBottom: 40}}>
            {/* THE OWNERS MAY EXTEND THIS RECORD'S FORM (Ed, 2026-10-29). Offered only when the server
                says this caller may: a member of the owning persona, on a draft, editable for the
                persona they act as. The designer is the same one a form is designed with. */}
            {design?.editable && !readOnly && (
                <div style={{display: "flex", alignItems: "center", gap: 12, marginBottom: 14}}>
                    <CCButton className={css_buttons.whiteStandardButton}
                              onClick={() => navigate(`/object/${objectRevisionUSID}/form-design`)}>
                        Design this record&rsquo;s form
                    </CCButton>
                    <Typography style={{fontSize: 12, color: Grey6}}>
                        Add questions, sections and layout to this record only. The change travels with the record&rsquo;s revisions.
                    </Typography>
                </div>
            )}
            <DivergencePanel divergence={divergence} promoting={promoting} notes={promotedNote}
                             readOnly={readOnly} onPromote={promote}
                             adopting={adopting} onAdopt={adopt}
                             record={{name: recordName, revisionUSID: objectRevisionUSID}}
                             labels={Object.fromEntries(drawn.fields.map(f => [f.Code, f.Label]))}/>

            {/* A FORM THAT IS BOUND AND EMPTY SAYS SO.
                `hasForm` on the record page is `!!RecordTypeRevisionUSID`, so the Form tab appears
                the moment a record TYPE is bound to a shape -- whether or not anybody has put
                fields in that shape. The five things filed in a project were bound on 2026-10-27
                with no fields on purpose, because inventing them would be writing somebody's
                quality system; without this they would have been five blank panes, which is the
                confusion Ed named once already on an empty attachment list.

                IT NAMES THE DATA CATALOG AND NOT THE FORM DESIGNER, because that is where the
                shape actually lives: these are composite DATA_TYPE rows, and DATA_TYPE is a t5
                table. Sending somebody to t1's form designer -- which serves FORM records, a
                different thing -- would be a door onto the wrong screen.

                ABOVE THE RENDERER RATHER THAN INSTEAD OF IT: the renderer is what reports how many
                fields there are, through onLoaded, so it has to mount. With none it draws nothing,
                and this is the whole pane. */}
            {reported && drawn.fields.length === 0 && (
                <div style={{marginBottom: 20, maxWidth: 820}}>
                    <Typography style={{fontSize: 14, fontWeight: 600, color: Grey17}}>
                        This record type has a form with no fields yet
                    </Typography>
                    <Typography style={{fontSize: 13, color: Grey7, lineHeight: 1.7,
                                        paddingTop: 6}}>
                        A form holds what your company wants to capture beyond what the record
                        already carries. Its fields are defined on the record type's shape in the
                        Data Catalog, and appear here once they exist.
                    </Typography>
                </div>
            )}

            {/* THE FORM, AND WHERE ITS QUESTIONS CAME FROM. The diagram is a sibling rather than
                part of the renderer: the renderer draws one record's contract and is used for
                previewing a bare shape too, which has no line to draw. */}
            <div style={{display: "flex", alignItems: "stretch"}}>
                {/* ROOM FOR THE OUTLINE. A lit section draws 10px OUTSIDE its own box, and an
                    outline takes no layout space -- so without this it runs over the pane divider
                    and under the gripper tab, which is exactly where the eye goes to find the
                    handle. The padding is the offset plus the stroke, with a little to spare. */}
                <div style={{flex: 1, minWidth: 0, paddingRight: 16}}>
                    <FormRenderer key={`form-${adoptTick}`}
                        objectRevisionUSID={objectRevisionUSID}
                        value={values}
                        onChange={next => { setEdited(next); setSaved(false); }}
                        readOnly={readOnly}
                        problems={problems}
                        onLoaded={next => { setDrawn(next); setReported(true); }}
                        highlightTypeUSID={lit.usid}
                        highlightColor={lit.colour}
                    />
                </div>

                {/* Drawn only when there IS a line. A record whose type has no parent gets no
                    pane and no handle, rather than a gripper onto an empty column. */}
                {drawn.fields.some(field => field.DeclaredBy) && (
                    // THE REF GOES ON THE WRAPPER, not on the content, so it is still attached when
                    // the pane is shut -- collapsed, this is the 1px handle, and its right edge is
                    // the anchor the drag measures from. On the content the ref would be null the
                    // moment the pane closed, and dragging it back open would do nothing.
                    <div ref={line.panelRef} style={{display: "flex", flexShrink: 0}}>
                        <ResizeGripper collapsed={line.collapsed} onMouseDown={line.onGripperDown}
                                       setCollapsed={line.setCollapsed} side="right"
                                       openLabel="Open the type line"/>
                        {!line.collapsed && (
                            <FormLineDiagram fields={drawn.fields} sections={drawn.sections}
                                             width={line.width}
                                             selected={lit.usid}
                                             onSelect={(usid, colour) =>
                                                 setLit({usid, colour: colour ?? "#1997D6"})}/>
                        )}
                    </div>
                )}
            </div>

            {error && (
                <Typography style={{fontSize: 13, color: "#B3261E", marginTop: 14}}>{error}</Typography>
            )}

            {!readOnly && (
                <div style={{display: "flex", alignItems: "center", gap: 14, marginTop: 24}}>
                    {/* DISABLED WITH NOTHING TO SAVE. Ed, on the curriculum panel: "When a change
                        is made, the save button should activate else it is disabled." Same rule,
                        same reason -- a Save that is always pressable teaches people that pressing
                        it means nothing, which is how one gets skipped before a Release. */}
                    <CCButton className={css_buttons.blueStandardButton} onClick={save}
                              disabled={saving || !touched}>
                        {saving ? "Saving…" : "Save"}
                    </CCButton>
                    {saved && (
                        <Typography style={{fontSize: 13, color: Grey6}}>Saved.</Typography>
                    )}
                    {problems.length > 0 && (
                        <Typography style={{fontSize: 13, color: "#B3261E"}}>
                            {problems.length} {problems.length === 1 ? "answer needs" : "answers need"} attention.
                        </Typography>
                    )}
                </div>
            )}

            {readOnly && (
                <Typography style={{fontSize: 13, color: Grey7, marginTop: 20}}>
                    {readOnlyReason(status)}
                </Typography>
            )}
        </div>
    );
}


/**
 * Why this revision cannot be edited, and what can be done about it -- if anything.
 *
 * IT USED TO TELL EVERY READ-ONLY RECORD TO REVISE IT. Only a RELEASED revision can be revised;
 * Revise requires RELEASED in the backend, and REVISION_ACTIONS says the same. So a DEPRECATED
 * record was told to do the one thing its own action bar does not offer, and a SUPERSEDED, CANCELLED
 * or VOID one -- which offer nothing at all -- was told the same.
 *
 * ASKS REVISION_ACTIONS RATHER THAN LISTING STATUSES. That map is what draws the buttons, so the
 * sentence and the buttons cannot disagree: if a status stops offering Revise, this stops promising
 * it, without anybody remembering to change two places.
 */
function readOnlyReason(status?: string | null): string {
    const name = String(status ?? "").toLowerCase();
    const actions = REVISION_ACTIONS[String(status ?? "").toUpperCase()] ?? [];

    if (actions.some(action => action.action === "Revise")) {
        return `This ${name} revision is read-only. Revise it to change the answers.`;
    }

    // Something can still be done to it, just not Revise -- Void on a deprecated one. Naming the
    // available move is more use than a flat refusal.
    if (actions.length > 0) {
        const labels = actions.map(action => action.label).join(" or ");
        return `This ${name} revision is read-only and cannot be revised. The only action left is `
            + `${labels}.`;
    }

    return `This ${name} revision is read-only. It is a final state -- nothing further can be done `
        + `to it.`;
}

/**
 * How this record's questions differ from the shape it was seeded from, and the way out.
 *
 * A DIVERGENCE HAS TO BE VISIBLE OR IT IS JUST MISSING DATA. A field only this record asks stores,
 * validates and projects perfectly well — and has no FIELD row anywhere, so nothing enumerates it,
 * no quality rule sees it, and a catalogue built from the shape has no column for it. It is real
 * data that is invisible to everything working from definitions.
 *
 * ONLY `Added` CAN BE PROMOTED. A removed field is a decision about this record; a changed one is
 * ambiguous — it is not clear whether the record or the shape is the thing that should move. Both
 * are still REPORTED, because silent is worse than unfixable.
 *
 * Promotion lands on a DRAFT of the shape, never the released one, and somebody releases it when
 * they mean to. This record is not touched: it already asks the question and already holds the
 * answer.
 */
const GUIDE_DIFFERS = "/docs/form-designer.html#differs";

/** What the user is told, per kind of difference: what happened, and what to do about it. Ed,
 *  2026-10-29: "the message given to the user is difficult to understand ... make it more
 *  understandable, give a cause, give a remedy." Each kind has a cause a person can follow and a
 *  remedy that is either a button here or a sentence saying who can do it. */
function Explanation({title, cause, remedy}: {title: string; cause: string; remedy: string}) {
    return (
        <div style={{marginTop: 10}}>
            <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17}}>{title}</Typography>
            <Typography style={{fontSize: 12, color: Grey6, marginTop: 2, maxWidth: "80ch"}}>
                <b style={{color: Grey17}}>Why:</b> {cause}
            </Typography>
            <Typography style={{fontSize: 12, color: Grey6, marginTop: 2, maxWidth: "80ch"}}>
                <b style={{color: Grey17}}>What to do:</b> {remedy}
            </Typography>
        </div>
    );
}

function DivergencePanel({divergence, promoting, notes, readOnly, onPromote, adopting, onAdopt, record, labels}: {
    divergence: any; promoting: string | null; notes: Record<string, string>;
    readOnly: boolean; onPromote: (code: string) => void;
    adopting: boolean; onAdopt: (codes: string[] | null) => void;
    record: {name: string; revisionUSID: string};
    labels: Record<string, string>;
}) {
    const pending: {Code: string; Name: string; Source: string}[] = divergence?.Pending ?? [];
    if (!divergence?.Diverged && !pending.length) return null;
    const label = (code: string) => labels[code] || code;
    const added: string[] = divergence?.Added ?? [];
    const removed: string[] = divergence?.Removed ?? [];
    const changed: string[] = divergence?.Changed ?? [];
    const who = record.name ? `"${record.name}"` : "this record";

    // THE ASSISTANT GETS THE CONTEXT THE SCREEN HAS: which record, which fields, which kind of
    // difference -- so its first answer is about this record, not about forms in general.
    const ask = () => {
        const parts: string[] = [];
        if (pending.length) parts.push(`it does not yet ask ${pending.map(p => `"${p.Name}"`).join(", ")}, which its form or shape now asks`);
        if (added.length) parts.push(`it asks ${added.map(label).map(l => `"${l}"`).join(", ")}, which its shape does not define`);
        if (changed.length) parts.push(`its field ${changed.map(label).map(l => `"${l}"`).join(", ")} is defined differently on the record than on its shape`);
        if (removed.length) parts.push(`it does not ask ${removed.map(label).map(l => `"${l}"`).join(", ")}, which its shape defines`);
        // PHRASED AS THE QUESTION THE GUIDE ANSWERS, with the record's particulars after it: the
        // assistant reaches for the documentation on "what does it mean when", and reaches for a
        // refusal code when handed a sentence that merely sounds like a problem.
        // NO APOSTROPHES IN THE QUESTION: a small model cut its search at the one in "record's".
        askAssistant(`What does it mean when the form of a record is not the same as the shape it was made from, and what can be done about it? I am looking at the form of ${who} (revision ${record.revisionUSID}); the screen says ${parts.join("; ")}.`);
    };

    return (
        <div style={{padding: "10px 14px", maxWidth: 900, marginBottom: 18,
                     border: "1px solid #E8C889", borderRadius: 5,
                     backgroundColor: "rgba(201,130,10,0.06)"}}>
            <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17}}>
                This record&rsquo;s form is not the same as the shape it was made from
            </Typography>
            <Typography style={{fontSize: 12, color: Grey6, marginTop: 4, maxWidth: "80ch"}}>
                Every record keeps its own copy of the form it was created with, so a form or shape changed
                later does not change records already made. The differences are listed below, each with why
                it is there and what can be done.{" "}
                <a href={GUIDE_DIFFERS} onClick={(e) => { e.preventDefault(); openGuide(GUIDE_DIFFERS); }}
                   style={{color: "#1976D2"}}>Read more in the guide</a>
                {" \u00b7 "}
                <a href="#ask" onClick={(e) => { e.preventDefault(); ask(); }} style={{color: "#1976D2"}}>Ask the assistant about this record</a>
            </Typography>

            {/* THE OTHER DIRECTION FIRST, because it is the common one. Ed, 2026-10-28: "Build
                the engine move so a shape's new question reaches existing records." */}
            {pending.length > 0 && (
                <div>
                    <Explanation
                        title={pending.length === 1 ? "A question was added after this record was made" : `${pending.length} questions were added after this record was made`}
                        cause={pending.every(one => one.Source === "Form")
                            ? "The form this record was created with has asked these since it was revised. This record still carries the earlier copy."
                            : "The shape this record was created from has defined these since. This record still carries the earlier copy."}
                        remedy={readOnly
                            ? "This record is released, so its form cannot change. Revise it; the new draft can take the questions up."
                            : "Press Take it up to add the question to this record, where the form places it. Nothing already answered changes."} />
                    <div style={{marginTop: 8}}>
                        {pending.map(one => (
                            <div key={one.Code} style={{display: "flex", alignItems: "center", gap: 10, marginTop: 4}}>
                                <Typography style={{fontSize: 13, color: Grey17, minWidth: 220}}>
                                    {one.Name} <span style={{color: Grey6}}>&middot; {one.Code}</span>
                                </Typography>
                                <CCButton className={css_buttons.whiteStandardButton}
                                          disabled={readOnly || adopting}
                                          onClick={() => onAdopt([one.Code])}>
                                    Take it up
                                </CCButton>
                            </div>
                        ))}
                        {pending.length > 1 && (
                            <div style={{marginTop: 8}}>
                                <CCButton className={css_buttons.blueStandardButton}
                                          disabled={readOnly || adopting}
                                          onClick={() => onAdopt(null)}>
                                    {adopting ? "Taking up\u2026" : `Take up all ${pending.length}`}
                                </CCButton>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {added.length > 0 && (
                <div>
                    <Explanation
                        title={added.length === 1 ? "A question only this record asks" : `${added.length} questions only this record asks`}
                        cause="Somebody added it to this record's form, or to the form it was made from before that form was released, and the shape never learned of it. The answer is stored and shown here, but nothing built from the shape knows the field exists: no picker lists it, no quality rule can use it, and a report built from the shape has no column for it."
                        remedy="Press Promote to put the field on a draft of the shape. Whoever releases that draft makes the field part of the shape for every record. This record is not changed by promoting." />
                    <div style={{marginTop: 8}}>
                        {added.map((code: string) => (
                            <div key={code} style={{display: "flex", alignItems: "center", gap: 10, marginTop: 4}}>
                                <Typography style={{fontSize: 13, color: Grey17, minWidth: 220}}>
                                    {label(code)} <span style={{color: Grey6}}>&middot; {code}</span>
                                </Typography>
                                {notes[code] ? (
                                    // Already done. Offering the button again would invite somebody to
                                    // press it until the panel changes, which it will not until the
                                    // shape's draft is released.
                                    <Typography style={{fontSize: 12, color: Grey6}}>{notes[code]}</Typography>
                                ) : (
                                    <CCButton className={css_buttons.whiteStandardButton}
                                              disabled={readOnly || promoting === code}
                                              onClick={() => onPromote(code)}>
                                        {promoting === code ? "Promoting\u2026" : "Promote to the shape"}
                                    </CCButton>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {changed.length > 0 && (
                <Explanation
                    title={`Defined differently on this record: ${changed.map(label).join(", ")}`}
                    cause="The shape, or the form this record was made from, was changed after this record was created, and this record kept the definition it was created with. The answers it holds fit its own definition, which is why they still show here."
                    remedy="An owner of this record decides, per field, in Design this record's form (above, on a draft): a save takes the shape's definition and brings this record into line -- check the answers the field holds afterwards, because they were given under the old one -- unless Keep this record's definition is ticked on that field, in which case the record keeps what it has. Ask the assistant above for what the difference is on this record." />
            )}

            {removed.length > 0 && (
                <Explanation
                    title={`Not asked on this record: ${removed.map(label).join(", ")}`}
                    cause="The form this record was made with does not ask these questions, although the shape defines them. That is a decision about this form, not an error."
                    remedy="Nothing is needed. If this record should answer them, revise the form to place them, or take them up here if they are offered above." />
            )}
        </div>
    );
}
